"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

export interface AccordionItem {
  id: string;
  title: string;
  content: React.ReactNode;
  description?: string;
}

export interface AccordionProps {
  items: AccordionItem[];
  multiple?: boolean;
  defaultOpen?: string[];
  className?: never;
}

export function Accordion({
  items,
  multiple = false,
  defaultOpen = [],
  className,
}: AccordionProps) {
  const [openItems, setOpenItems] = React.useState<Set<string>>(
    new Set(defaultOpen)
  );

  const toggle = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(multiple ? prev : []);
      if (prev.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className={cn("space-y-2", className)}>
      {items.map((item) => {
        const isOpen = openItems.has(item.id);

        return (
          <div
            key={item.id}
            className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white/90 dark:border-zinc-800 dark:bg-zinc-900/85"
          >
            <button
              onClick={() => toggle(item.id)}
              className="flex w-full items-center justify-between px-4 py-3.5 text-left transition-colors hover:bg-slate-50/80 dark:hover:bg-zinc-800/50"
            >
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-slate-900 dark:text-white">
                  {item.title}
                </div>
                {item.description && (
                  <div className="text-xs text-slate-500 dark:text-zinc-400">
                    {item.description}
                  </div>
                )}
              </div>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 dark:text-zinc-400",
                  isOpen && "rotate-180"
                )}
              />
            </button>

            <div
              className={cn(
                "overflow-hidden transition-all duration-300 ease-in-out",
                isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
              )}
            >
              <div className="border-t border-slate-200/70 px-4 py-3.5 text-sm text-slate-700 dark:border-zinc-800 dark:text-zinc-300">
                {item.content}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
